import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  UtensilsCrossed, 
  Wine, 
  Users, 
  Building2,
  TrendingUp,
  TrendingDown,
  Euro,
  BarChart3,
  Target,
  CalendarDays,
  Package,
  Settings,
  Plus,
  X,
  CloudSun,
  Brain,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Truck,
  FileText,
  Loader2,
  RefreshCw,
  LayoutGrid,
  Save,
  GripVertical,
  ChevronRight,
  ShoppingCart,
  Clock,
  Coffee,
  Pizza,
  Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import { Button } from "../components/ui/button";
import { useStandort } from "../hooks/useStandort";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#8B5CF6', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

// Widget-Definitionen
const WIDGET_DEFS = {
  wetter: { name: "Wetter & Empfehlungen", icon: CloudSun, cols: 2 },
  gewinn: { name: "Monatlicher Gewinn", icon: Wallet, cols: 2 },
  umsatz: { name: "Heute Umsatz", icon: Euro, cols: 1 },
  tagesumsatz: { name: "Tagesumsatz Details", icon: BarChart3, cols: 2 },
  verkaeufe: { name: "Verkaufte Artikel", icon: ShoppingCart, cols: 2 },
  speisen: { name: "Speisen", icon: UtensilsCrossed, cols: 1 },
  getraenke: { name: "Getränke", icon: Wine, cols: 1 },
  mitarbeiter: { name: "Personal", icon: Users, cols: 1 },
  fixkosten: { name: "Fixkosten", icon: Building2, cols: 1 },
  kosten_chart: { name: "Kostenverteilung", icon: BarChart3, cols: 2 },
  umsatz_chart: { name: "Umsatz Verlauf", icon: TrendingUp, cols: 2 },
  quick_actions: { name: "Schnellzugriff", icon: Sparkles, cols: 2 },
  ki_empfehlung: { name: "KI-Empfehlungen", icon: Brain, cols: 2 },
};

const DEFAULT_LAYOUT = ["wetter", "gewinn", "tagesumsatz", "verkaeufe", "umsatz", "speisen", "getraenke", "mitarbeiter", "fixkosten", "quick_actions"];

export const Dashboard = () => {
  const navigate = useNavigate();
  const { activeStandortId } = useStandort();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [layout, setLayout] = useState(() => {
    try {
      const saved = localStorage.getItem("margevio-dashboard-layout");
      return saved ? JSON.parse(saved) : DEFAULT_LAYOUT;
    } catch {
      return DEFAULT_LAYOUT;
    }
  });
  
  const [summary, setSummary] = useState(null);
  const [verteilung, setVerteilung] = useState([]);
  const [wetter, setWetter] = useState(null);
  const [umsatzVerlauf, setUmsatzVerlauf] = useState([]);
  const [tagesumsatz, setTagesumsatz] = useState(null);
  const [verkaeufe, setVerkaeufe] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStandortId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const results = await Promise.allSettled([
        axios.get(`${API}/dashboard/summary`, { params, withCredentials: true }),
        axios.get(`${API}/dashboard/kosten-verteilung`, { params, withCredentials: true }),
        axios.get(`${API}/wetter/betrieb`, { withCredentials: true }),
        axios.get(`${API}/dashboard/tagesumsatz`, { params, withCredentials: true }),
        axios.get(`${API}/dashboard/verkaeufe-heute`, { withCredentials: true }),
      ]);
      
      setSummary(results[0].status === 'fulfilled' ? results[0].value.data : null);
      setVerteilung(results[1].status === 'fulfilled' ? results[1].value.data || [] : []);
      setWetter(results[2].status === 'fulfilled' ? results[2].value.data : null);
      setTagesumsatz(results[3].status === 'fulfilled' ? results[3].value.data : null);
      setVerkaeufe(results[4].status === 'fulfilled' ? results[4].value.data || [] : []);
      
      setUmsatzVerlauf([
        { tag: "Mo", umsatz: 1200 },
        { tag: "Di", umsatz: 980 },
        { tag: "Mi", umsatz: 1450 },
        { tag: "Do", umsatz: 1680 },
        { tag: "Fr", umsatz: 2340 },
        { tag: "Sa", umsatz: 2890 },
        { tag: "So", umsatz: 1560 },
      ]);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const saveLayout = () => {
    localStorage.setItem("margevio-dashboard-layout", JSON.stringify(layout));
    setIsEditing(false);
    toast.success("Dashboard-Layout gespeichert!");
  };

  const resetLayout = () => {
    setLayout(DEFAULT_LAYOUT);
    localStorage.setItem("margevio-dashboard-layout", JSON.stringify(DEFAULT_LAYOUT));
    toast.success("Layout zurückgesetzt");
  };

  const addWidget = (widgetId) => {
    if (!layout.includes(widgetId)) {
      setLayout([...layout, widgetId]);
    }
    setAddWidgetOpen(false);
  };

  const removeWidget = (widgetId) => {
    setLayout(layout.filter(id => id !== widgetId));
  };

  const moveWidget = (index, direction) => {
    const newLayout = [...layout];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newLayout.length) {
      [newLayout[index], newLayout[newIndex]] = [newLayout[newIndex], newLayout[index]];
      setLayout(newLayout);
    }
  };

  // Widget Renderer
  const renderWidget = (widgetId, index) => {
    const def = WIDGET_DEFS[widgetId];
    if (!def) return null;
    
    const colClass = def.cols === 2 ? "col-span-2" : "col-span-1";
    
    const wrapperClass = `
      relative group
      ${colClass}
      bg-gradient-to-br from-slate-800/80 to-slate-900/90
      border border-slate-700/50 rounded-xl p-4
      hover:border-violet-500/30 transition-all
      ${isEditing ? 'ring-2 ring-violet-500/20' : ''}
    `;

    const content = (() => {
      switch (widgetId) {
        case "wetter":
          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <CloudSun size={16} className="text-yellow-400" />
                  Wetter & Empfehlungen
                </h3>
                <span className="text-slate-400 text-xs">{wetter?.standort || "Limburg"}</span>
              </div>
              {wetter ? (
                <>
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-4xl">{wetter.wetter_icon || wetter.wetter?.icon || "☁️"}</span>
                    <div>
                      <p className="text-2xl font-bold text-white">{wetter.temperatur ?? wetter.wetter?.temperatur ?? "--"}°C</p>
                      <p className="text-sm text-slate-400">{wetter.wetter_label || wetter.wetter?.label || ""}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {wetter.empfehlungen?.slice(0, 3).map((emp, i) => (
                      <p key={i} className="text-xs text-slate-300 bg-slate-700/50 rounded px-2 py-1">{emp}</p>
                    )) || (
                      <p className="text-xs text-slate-400">Gefühlt: {wetter.gefuehlt ?? "--"}°C, Wind: {wetter.windgeschwindigkeit ?? "--"} km/h</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-24 text-slate-500">
                  <Loader2 className="animate-spin" size={20} />
                </div>
              )}
            </div>
          );

        case "umsatz":
          return (
            <div className="cursor-pointer" onClick={() => navigate("/einnahmen")}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 mb-1">Heute Umsatz</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(summary?.gesamt?.umsatz_heute || 0)}</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-xl">
                  <Euro size={24} className="text-green-400" />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-green-400 flex items-center gap-1">
                  <TrendingUp size={12} /> +5%
                </span>
                <span className="text-slate-500">vs. Vortag</span>
              </div>
            </div>
          );

        case "gewinn":
          const gewinn = summary?.gesamt?.gewinn_monat || 0;
          const umsatzAvg = summary?.gesamt?.umsatz_monat_avg || 0;
          const kostenGesamt = summary?.gesamt?.kosten_monatlich || 0;
          const isPositiv = gewinn > 0;
          const monateErfasst = summary?.gesamt?.monate_erfasst || 0;
          return (
            <div className="cursor-pointer" onClick={() => navigate("/ki-assistent")} data-testid="gewinn-widget">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Wallet size={16} className="text-emerald-400" />
                  Monatlicher Gewinn
                </h3>
                <span className="text-xs text-slate-400">{monateErfasst} Monate Basis</span>
              </div>
              <div className="flex items-end gap-4 mb-3">
                <div>
                  <p className={`text-3xl font-bold ${isPositiv ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(gewinn)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">pro Monat (Durchschnitt)</p>
                </div>
                <div className={`p-3 rounded-xl ${isPositiv ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                  {isPositiv ? <TrendingUp size={24} className="text-emerald-400" /> : <TrendingDown size={24} className="text-red-400" />}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-slate-400">Umsatz</p>
                  <p className="text-xs font-semibold text-green-400">{formatCurrency(umsatzAvg)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-slate-400">Kosten</p>
                  <p className="text-xs font-semibold text-red-400">{formatCurrency(kostenGesamt)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-slate-400">Marge</p>
                  <p className={`text-xs font-semibold ${isPositiv ? 'text-emerald-400' : 'text-red-400'}`}>
                    {umsatzAvg > 0 ? ((gewinn / umsatzAvg) * 100).toFixed(1) : '0'}%
                  </p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1 text-[10px] text-violet-400">
                <Brain size={10} /> KI-Assistent für detaillierte Analyse
                <ChevronRight size={10} />
              </div>
            </div>
          );

        case "tagesumsatz":
          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Clock size={16} className="text-cyan-400" />
                  Tagesumsatz Details
                </h3>
                <span className="text-slate-400 text-xs">{new Date().toLocaleDateString('de-DE')}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <p className="text-xs text-slate-400">Speisen</p>
                  <p className="text-lg font-bold text-emerald-400">{formatCurrency(tagesumsatz?.speisen || 0)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <p className="text-xs text-slate-400">Getränke</p>
                  <p className="text-lg font-bold text-violet-400">{formatCurrency(tagesumsatz?.getraenke || 0)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <p className="text-xs text-slate-400">Gesamt</p>
                  <p className="text-lg font-bold text-green-400">{formatCurrency(tagesumsatz?.gesamt || 0)}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400 flex items-center gap-1"><Coffee size={12} /> Frühschicht</span>
                  <span className="text-white font-mono">{formatCurrency(tagesumsatz?.fruehschicht || 0)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400 flex items-center gap-1"><Pizza size={12} /> Mittagsschicht</span>
                  <span className="text-white font-mono">{formatCurrency(tagesumsatz?.mittagsschicht || 0)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400 flex items-center gap-1"><Wine size={12} /> Abendschicht</span>
                  <span className="text-white font-mono">{formatCurrency(tagesumsatz?.abendschicht || 0)}</span>
                </div>
              </div>
              {!tagesumsatz && (
                <div className="text-center py-4 text-slate-500 text-sm">
                  <p>Noch keine Umsätze heute</p>
                  <button onClick={() => navigate("/einnahmen")} className="text-violet-400 text-xs mt-1">+ Einnahmen erfassen</button>
                </div>
              )}
            </div>
          );

        case "verkaeufe":
          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <ShoppingCart size={16} className="text-amber-400" />
                  Verkaufte Artikel heute
                </h3>
                <span className="text-xs text-slate-400">{verkaeufe?.length || 0} Artikel</span>
              </div>
              {verkaeufe && verkaeufe.length > 0 ? (
                <div className="space-y-2 max-h-[180px] overflow-y-auto">
                  {verkaeufe.slice(0, 8).map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded ${v.typ === 'speise' ? 'bg-emerald-500/20' : 'bg-violet-500/20'}`}>
                          {v.typ === 'speise' ? <UtensilsCrossed size={12} className="text-emerald-400" /> : <Wine size={12} className="text-violet-400" />}
                        </div>
                        <div>
                          <p className="text-xs text-white font-medium">{v.name}</p>
                          <p className="text-[10px] text-slate-500">{v.anzahl}x verkauft</p>
                        </div>
                      </div>
                      <span className="text-xs text-green-400 font-mono">{formatCurrency(v.umsatz)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <ShoppingCart size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Noch keine Verkäufe heute</p>
                  <button onClick={() => navigate("/einnahmen")} className="text-violet-400 text-xs mt-2">+ Verkauf erfassen</button>
                </div>
              )}
            </div>
          );

        case "speisen":
        case "getraenke":
        case "mitarbeiter":
        case "fixkosten":
          const kpiConfig = {
            speisen: { title: "Speisen", value: summary?.speisen?.anzahl || 0, sub: `Ø ${summary?.speisen?.durchschnitt_marge_prozent || 0}% Marge`, color: "bg-emerald-500", link: "/speisen" },
            getraenke: { title: "Getränke", value: summary?.getraenke?.anzahl || 0, sub: `Ø ${summary?.getraenke?.durchschnitt_marge_prozent || 0}% Marge`, color: "bg-violet-500", link: "/getraenke" },
            mitarbeiter: { title: "Personal", value: summary?.mitarbeiter?.anzahl || 0, sub: formatCurrency(summary?.mitarbeiter?.gesamt_kosten_monatlich) + "/Mo", color: "bg-blue-500", link: "/mitarbeiter" },
            fixkosten: { title: "Fixkosten", value: formatCurrency(summary?.fixkosten?.gesamt_monatlich), sub: "pro Monat", color: "bg-amber-500", link: "/fixkosten" },
          }[widgetId];
          const Icon = def.icon;
          return (
            <div className="cursor-pointer" onClick={() => navigate(kpiConfig.link)}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 mb-1">{kpiConfig.title}</p>
                  <p className="text-xl font-bold text-white">{kpiConfig.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{kpiConfig.sub}</p>
                </div>
                <div className={`p-3 ${kpiConfig.color} rounded-xl`}>
                  <Icon size={20} className="text-white" />
                </div>
              </div>
            </div>
          );

        case "kosten_chart":
          const total = verteilung?.reduce((sum, item) => sum + item.wert, 0) || 0;
          return (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-white">Kostenverteilung</h3>
                <button onClick={() => navigate("/statistik")} className="text-xs text-violet-400 hover:text-violet-300">
                  Details →
                </button>
              </div>
              {verteilung && verteilung.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="w-1/2 h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={verteilung}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={60}
                          paddingAngle={2}
                          dataKey="wert"
                          stroke="none"
                        >
                          {verteilung.map((entry, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          content={({ active, payload }) => active && payload?.length ? (
                            <div className="bg-slate-800 border border-slate-700 px-3 py-2 rounded-lg">
                              <p className="text-white text-sm">{payload[0].name}</p>
                              <p className="text-violet-400 font-mono">{formatCurrency(payload[0].value)}</p>
                            </div>
                          ) : null}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-1/2 space-y-1.5">
                    {verteilung.slice(0, 5).map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i] }} />
                        <span className="text-xs text-slate-300 flex-1 truncate">{item.name}</span>
                        <span className="text-xs text-slate-400">{total > 0 ? ((item.wert / total) * 100).toFixed(0) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[160px] text-slate-500 text-sm">
                  Keine Kostendaten
                </div>
              )}
            </div>
          );

        case "umsatz_chart":
          return (
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">Umsatz der letzten 7 Tage</h3>
              <div className="h-[160px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={umsatzVerlauf}>
                    <defs>
                      <linearGradient id="umsatzGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="tag" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                    <Area type="monotone" dataKey="umsatz" stroke="#8B5CF6" fill="url(#umsatzGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          );

        case "quick_actions":
          const actions = [
            { label: "Einnahmen", icon: Euro, color: "bg-green-500/20 text-green-400 hover:bg-green-500/30", link: "/einnahmen" },
            { label: "Strategie", icon: Target, color: "bg-violet-500/20 text-violet-400 hover:bg-violet-500/30", link: "/strategie" },
            { label: "Schichtplan", icon: CalendarDays, color: "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30", link: "/schichtplan" },
            { label: "Import", icon: FileText, color: "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30", link: "/import" },
            { label: "KI", icon: Brain, color: "bg-pink-500/20 text-pink-400 hover:bg-pink-500/30", link: "/ki-assistent" },
            { label: "Lager", icon: Package, color: "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30", link: "/warenwirtschaft" },
          ];
          return (
            <div>
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Sparkles size={14} className="text-yellow-400" />
                Schnellzugriff
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {actions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(action.link)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${action.color} transition-colors text-xs`}
                  >
                    <action.icon size={14} />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );

        case "ki_empfehlung":
          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Brain size={16} className="text-violet-400" />
                  KI-Empfehlungen
                </h3>
                <button onClick={() => navigate("/ki-assistent")} className="text-xs text-violet-400">Mehr →</button>
              </div>
              <div className="space-y-2">
                {wetter?.empfehlungen?.slice(0, 3).map((emp, i) => (
                  <div key={i} className="p-2 bg-violet-900/20 border border-violet-700/30 rounded-lg">
                    <p className="text-xs text-slate-300">{emp}</p>
                  </div>
                )) || (
                  <p className="text-slate-500 text-sm text-center py-4">Keine Empfehlungen verfügbar</p>
                )}
              </div>
            </div>
          );

        default:
          return null;
      }
    })();

    return (
      <div key={widgetId} className={wrapperClass}>
        {isEditing && (
          <div className="absolute -top-2 -right-2 z-10 flex gap-1">
            <button
              onClick={() => moveWidget(index, -1)}
              className="p-1 bg-slate-600 rounded text-white hover:bg-slate-500 text-xs"
              disabled={index === 0}
            >
              ←
            </button>
            <button
              onClick={() => moveWidget(index, 1)}
              className="p-1 bg-slate-600 rounded text-white hover:bg-slate-500 text-xs"
              disabled={index === layout.length - 1}
            >
              →
            </button>
            <button
              onClick={() => removeWidget(widgetId)}
              className="p-1 bg-red-500 rounded text-white hover:bg-red-600"
            >
              <X size={12} />
            </button>
          </div>
        )}
        {content}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
          <p className="text-slate-400">Dashboard wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 text-sm">Übersicht Ihrer Gastronomie</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-400">
            <RefreshCw size={16} />
          </Button>
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setAddWidgetOpen(true)} className="border-slate-600">
                <Plus size={16} className="mr-1" /> Widget
              </Button>
              <Button variant="outline" size="sm" onClick={resetLayout} className="border-slate-600">
                <LayoutGrid size={16} className="mr-1" /> Reset
              </Button>
              <Button size="sm" onClick={saveLayout} className="bg-violet-600 hover:bg-violet-700">
                <Save size={16} className="mr-1" /> Speichern
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="border-slate-600">
              <Settings size={16} className="mr-1" /> Anpassen
            </Button>
          )}
        </div>
      </div>

      {/* Info-Banner wenn keine Daten */}
      {(!summary || (summary.speisen?.anzahl === 0 && summary.getraenke?.anzahl === 0)) && (
        <div className="mb-6 p-4 bg-violet-900/20 border border-violet-700/50 rounded-xl">
          <div className="flex items-start gap-3">
            <Sparkles className="text-violet-400 mt-0.5" size={20} />
            <div>
              <h3 className="text-violet-300 font-semibold">Willkommen bei MargeVio!</h3>
              <p className="text-slate-400 text-sm mt-1">
                Starten Sie eine Demo, um das Dashboard mit realistischen Daten zu sehen:
              </p>
              <Button onClick={() => navigate("/")} className="mt-3 bg-violet-600 hover:bg-violet-700" size="sm">
                Demo starten →
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Widget Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {layout.map((widgetId, index) => renderWidget(widgetId, index))}
      </div>

      {/* Widget hinzufügen Dialog */}
      <Dialog open={addWidgetOpen} onOpenChange={setAddWidgetOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Widget hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {Object.entries(WIDGET_DEFS)
              .filter(([id]) => !layout.includes(id))
              .map(([id, def]) => (
                <button
                  key={id}
                  onClick={() => addWidget(id)}
                  className="flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors text-left"
                >
                  <def.icon size={20} className="text-violet-400" />
                  <span className="text-sm text-white">{def.name}</span>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
